import * as React from 'react'
import { useMemo } from 'react'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Button,
    Field,
    makeStyles,
    Menu,
    MenuItemCheckbox,
    MenuItemRadio,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    tokens,
    Tooltip,
} from '@fluentui/react-components'
import useModules from '../../hooks/module/useModules'
import useRoles, { rolesTasks } from '../../hooks/useRoles'
import { useTranslation } from 'react-i18next'
import useUser from '../../hooks/useUser'
import useViewport, { Viewport } from '../../hooks/useViewport'
import UserPicker from '../../components/TagPicker/UserPicker'
import { ModuleType } from '../../context/GlobalContext'
import AttributesList from '../../components/Form/Attributes/AttributesList'
import { AttributeContextType, AttributeEntityDto, AttributeViewType, EntityAttributeType } from '../../hooks/useAttributes'
import { useFormikContext } from 'formik'
import useAxios from '../../hooks/useAxios'
import { SortDirection } from '../../config/settingConfig'
import { AxiosResponse } from 'axios'
import BaseOverview, {
    BaseOverviewCount,
    BaseOverviewLineItemProps,
    BaseOverviewProps,
    parseFilterValuesToParams,
    useOverviewStyles,
} from '../BaseOverview/BaseOverview'
import {
    BaseIntervalOverviewFilterType,
    BaseOverviewFilterProps,
    generateFromDate,
    generateToDate,
    ViewType,
} from '../BaseOverview/BaseOverviewFilter'
import { compareDate } from '../../helpers/dateHelper'
import { BaseOverviewSortingType } from '../BaseOverview/BaseSortMenu'
import { TaskOverviewDto } from '../../config/taskConfig'
import TaskStatusPicker from '../../components/TagPicker/TaskStatusPicker'
import { CalendarArrowRightRegular, CalendarDayRegular, CalendarWorkWeekRegular, FlagRegular } from '@fluentui/react-icons'
import { DateTime } from 'luxon'
import DateRange from '../../components/DatePicker/DateRange'
import FilePicker from '../../components/TagPicker/FilePicker'
import { MenuItemKey } from '../../components/MainMenu'
import useTaskStatuses from '../../hooks/useTaskStatuses'
import BooleanCombobox from '../../components/Combobox/BooleanCombobox'
import { CheckboxOnChangeData } from '@fluentui/react-checkbox'
import BaseHeaderItem from '../BaseOverview/BaseHeaderItem'
import HeaderActionsButtons from './HeaderActionsButtons'
import TaskListItem from './TaskListItem'
import { UserStatus } from '../../config/userConfig'
import BaseIntervalFilterMenu from '../BaseOverview/BaseIntervalFilterMenu'
import PartyPicker from '../../components/TagPicker/PartyPicker'

export const useItemStyles = makeStyles({
    taskItem: {
        '& button[aria-label="flag-action"]': {
            visibility: 'hidden',
            height: '24px',
        },
        '& button[aria-label="flag-highlight-active"]': {
            boxShadow: '1px 1px #888888',
            height: '24px',
        },
        '& button[aria-label="flag-today-active"]': {
            boxShadow: '1px 1px #888888',
            height: '24px',
        },
        '& button[aria-label="flag-week-active"]': {
            boxShadow: '1px 1px #888888',
            height: '24px',
        },

        '&:hover': {
            '& button[aria-label="flag-action"]': {
                visibility: 'visible',
            },
        },
    },
    listTaskItem: {
        display: 'grid',
        gap: tokens.spacingVerticalS,
        gridTemplateColumns:
            'var(--baseOverviewAvatarWidth) var(--baseOverviewIconWidth) var(--baseOverviewDateWidth) auto var(--baseOverviewIconWidth) var(--baseOverviewIconWidth) var(--baseOverviewIconWidth) var(--baseOverviewFileWidth) var(--baseOverviewAvatarWidth)',
        gridTemplateRows: '1fr',
        alignItems: 'center',
    },
    listTaskItemSmall: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'min(24px,auto) min(24px,auto) 24px',
    },
    listTaskItemSmallRow1: {
        display: 'grid',
        gridTemplateColumns: 'var(--baseOverviewAvatarWidth) auto',
        gap: tokens.spacingHorizontalS,
    },
    listTaskItemSmallRow2: {
        display: 'grid',
        gridTemplateColumns: 'var(--baseOverviewTimeWidth) var(--baseOverviewIconWidth) auto',
        gap: tokens.spacingHorizontalS,
    },
    listTaskItemSmallRow3: {
        display: 'grid',
        gridTemplateColumns:
            'var(--baseOverviewDateWidth) var(--baseOverviewIconWidth) var(--baseOverviewIconWidth) var(--baseOverviewIconWidth) auto var(--baseOverviewAvatarWidth)',
        gap: tokens.spacingHorizontalS,
        alignItems: 'center',
    },
    listTaskItemHighlight: {
        border: '1px solid var(--colorPalettePeachBorderActive)!important',
    },
    listTaskItemOverflow: {
        overflowWrap: 'anywhere',
    },
    listTaskItemTitle: {
        display: 'grid',
        gridTemplateColumns: 'var(--baseOverviewTimeWidth) 1fr 1fr',
        gap: tokens.spacingHorizontalS,
    },
    listTaskItemMenuButton: {
        padding: tokens.spacingHorizontalXS,
        display: 'flex',
        justifyContent: 'center',

        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground2,
            borderRadius: tokens.borderRadiusMedium,
        },
    },
    filterTagHighlight: {
        color: tokens.colorPalettePeachBorderActive,
    },
    filterTagToday: {
        color: tokens.colorPaletteRedForeground3,
    },
    filterTagWeek: {
        color: tokens.colorPaletteBlueBorderActive,
    },
    workflowColor: {
        color: tokens.colorPaletteBerryForeground1,
    },
})

export type TaskOverviewProps = BaseOverviewProps<ExtendedTaskOverviewDto, TaskFilterType, TaskSortingType>

export type BaseTaskOverviewProps = Partial<TaskOverviewProps> & {
    baseUrl?: string

    breadcrumb?: MenuItemKey
    fileId?: string
    crmRelationId?: string
}

export type BaseTaskOverviewFilterProps = BaseOverviewFilterProps<ExtendedTaskOverviewDto, TaskFilterType, TaskSortingType> & BaseTaskOverviewProps

export type BaseTaskOverviewLineItemProps = BaseTaskOverviewProps &
    BaseOverviewLineItemProps<ExtendedTaskOverviewDto, TaskFilterType, TaskSortingType> & {
        listItemSmall: boolean
    }

export type ExtendedTaskOverviewDto = TaskOverviewDto

export type TaskFilterType = AttributeEntityDto &
    BaseIntervalOverviewFilterType & {
        assignees?: string[]
        assigners?: string[]
        files?: string[]
        crmRelations?: string[]
        statuses?: string[]
        highlight?: boolean
        today?: boolean
        week?: boolean
        excludeOwnerIsAssignee?: boolean
        priorityTag?: string
    }

export type TaskSorting = {
    id: string
    due: string
    title: string
    status: string
    assignee: string
    assigner: string
    'file.reference': string
}

export type TaskSortingType = BaseOverviewSortingType<TaskSorting>

function TaskFilter(props: BaseTaskOverviewFilterProps) {
    const { values, setFieldValue } = useFormikContext<TaskFilterType>()
    const modules = useModules()
    const { t: translate } = useTranslation()

    const orientation = useMemo(() => (props.viewportMedium ? 'vertical' : 'horizontal'), [props.viewportMedium])

    const updateFieldValue = (field: string, value: any, shouldValidate?: boolean) => {
        setFieldValue('tag', undefined, false).then(() => setFieldValue(field, value, shouldValidate))
    }

    return (
        <>
            <DateRange
                orientation={orientation}
                startValue={values.from}
                endValue={values.to}
                changeStartDate={date => updateFieldValue('from', date)}
                changeEndDate={date => updateFieldValue('to', date)}
                showLabel
            />
            <Field label={translate('task.filter.assignee')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.assignees || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('assignees', selectedOptions)}
                />
            </Field>
            <Field label={translate('task.filter.assigner')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.assigners || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('assigners', selectedOptions)}
                />
            </Field>
            {!props.fileId && (
                <Field label={translate('task.filter.file')} orientation={orientation}>
                    <FilePicker
                        selectedOptions={values.files || []}
                        onOptionsSelect={(_, selectedOptions) => updateFieldValue('files', selectedOptions)}
                    />
                </Field>
            )}
            {!props.crmRelationId && modules.has(ModuleType.CRM) && (
                <Field label={translate('file.filter.crmrelation')} orientation={orientation}>
                    <PartyPicker
                        selectedOptions={values.crmRelations || []}
                        onOptionsSelect={(_, selectedOptions) => updateFieldValue('crmRelations', selectedOptions)}
                        params={{ crmRelation: true }}
                    />
                </Field>
            )}
            <Field label={translate('task.filter.status.label')} orientation={orientation}>
                <TaskStatusPicker
                    selectedOptions={values.statuses || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('statuses', selectedOptions)}
                />
            </Field>
            <Field label={translate('task.filter.highlight')} orientation={orientation}>
                <BooleanCombobox
                    selectedOptions={[values.highlight?.toString() || 'mixed']}
                    onOptionSelect={(_, data: CheckboxOnChangeData) =>
                        updateFieldValue('highlight', data.checked === 'mixed' ? undefined : data.checked)
                    }
                />
            </Field>
            <Field label={translate('task.filter.today')} orientation={orientation}>
                <BooleanCombobox
                    selectedOptions={[values.today?.toString() || 'mixed']}
                    onOptionSelect={(_, data: CheckboxOnChangeData) => updateFieldValue('today', data.checked === 'mixed' ? undefined : data.checked)}
                />
            </Field>
            <Field label={translate('task.filter.week')} orientation={orientation}>
                <BooleanCombobox
                    selectedOptions={[values.week?.toString() || 'mixed']}
                    onOptionSelect={(_, data: CheckboxOnChangeData) => updateFieldValue('week', data.checked === 'mixed' ? undefined : data.checked)}
                />
            </Field>
            {modules.has(ModuleType.Attributes) && (
                <Accordion collapsible>
                    <AccordionItem value="attributes">
                        <AccordionHeader>{translate('task.filter.attributes')}</AccordionHeader>
                        <AccordionPanel>
                            <AttributesList
                                orientation={orientation}
                                entityType={EntityAttributeType.File}
                                context={[AttributeContextType.File]}
                                viewType={AttributeViewType.Filter}
                                entity={values}
                                setFieldValue={(field, value) => updateFieldValue(values[field], value)}
                            />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            )}
        </>
    )
}

const intervalFilterItems: TaskFilterType[] = [
    {
        from: generateFromDate('history'),
        to: generateToDate('history'),
        tag: 'history',
        icon: <CalendarDayRegular />,
    },
    {
        from: generateFromDate('history7days'),
        to: generateToDate('history7days'),
        tag: '7days',
        icon: <CalendarWorkWeekRegular />,
    },
    {
        from: generateFromDate('all'),
        to: generateToDate('all'),
        tag: 'all',
        icon: <CalendarArrowRightRegular />,
    },
]

const headerSortingItems: TaskSortingType[] = [
    { property: ['due', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.dateasc', default: true },
    { property: ['due', 'id'], direction: SortDirection.Descending, descriptionKey: 'sorting.datedesc' },
    { property: ['title', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.titleaz' },
    { property: ['title', 'id'], direction: SortDirection.Descending, descriptionKey: 'sorting.titleza' },
    { property: ['file.reference', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.referenceasc' },
    {
        property: ['file.reference', 'id'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.referencedesc',
    },
]

function HeaderFilterTags(props: BaseTaskOverviewFilterProps) {
    const { values, setValues } = useFormikContext<TaskFilterType>()
    const overviewStyles = useOverviewStyles()
    const { taskStatuses, getStatusName } = useTaskStatuses()
    const { t: translate } = useTranslation()

    const checkedValues = useMemo<Record<string, string[]>>(
        () => ({
            priority: values.priorityTag ? [`${values.priorityTag}`] : [],
            statuses: values.statuses ? values.statuses : [],
        }),
        [values.tag, values.statuses],
    )

    return (
        <div className={overviewStyles.flexGapXS}>
            <BaseIntervalFilterMenu {...props} filterItems={intervalFilterItems} />

            <Menu positioning={{ autoSize: true }} checkedValues={checkedValues}>
                <MenuTrigger disableButtonEnhancement>
                    <Tooltip withArrow relationship={'label'} content={translate('task.filter.priority.tooltip')}>
                        <Button
                            appearance={'secondary'}
                            size={'small'}
                            className={mergeClasses(overviewStyles.filterTagButton, values.priorityTag && overviewStyles.filterTagButtonSelected)}
                            icon={!values.priorityTag ? <FlagRegular /> : undefined}
                        >
                            {values.priorityTag && translate(`task.filter.${values.priorityTag}`)}
                        </Button>
                    </Tooltip>
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        <MenuItemRadio
                            key={1}
                            name={'priority'}
                            value={`highlight`}
                            onClick={() =>
                                setValues({
                                    ...values,
                                    from: undefined,
                                    to: undefined,
                                    highlight: values.highlight ? undefined : true,
                                    today: undefined,
                                    week: undefined,
                                    tag: undefined,
                                    priorityTag: !values.highlight ? 'highlight' : undefined,
                                })
                            }
                        >
                            {translate('task.filter.highlight')}
                        </MenuItemRadio>
                        <MenuItemRadio
                            key={2}
                            name={'priority'}
                            value={`today`}
                            onClick={() =>
                                setValues({
                                    ...values,
                                    from: undefined,
                                    to: undefined,
                                    highlight: undefined,
                                    today: values.today ? undefined : true,
                                    week: undefined,
                                    tag: undefined,
                                    priorityTag: !values.today ? 'today' : undefined,
                                })
                            }
                        >
                            {translate('task.filter.today')}
                        </MenuItemRadio>
                        <MenuItemRadio
                            key={3}
                            name={'priority'}
                            value={`week`}
                            onClick={() => {
                                setValues({
                                    ...values,
                                    from: undefined,
                                    to: undefined,
                                    highlight: undefined,
                                    today: undefined,
                                    week: values.week ? undefined : !values.week,
                                    tag: undefined,
                                    priorityTag: !values.week ? 'week' : undefined,
                                })
                            }}
                        >
                            {translate('task.filter.week')}
                        </MenuItemRadio>
                    </MenuList>
                </MenuPopover>
            </Menu>

            {props.viewType === ViewType.ListView && (
                <Menu positioning={{ autoSize: true }} checkedValues={checkedValues}>
                    <MenuTrigger disableButtonEnhancement>
                        <Tooltip withArrow relationship={'label'} content={translate('task.filter.status.tooltip')}>
                            <Button
                                appearance={'secondary'}
                                size={'small'}
                                className={mergeClasses(overviewStyles.filterTagButton, values.statuses && overviewStyles.filterTagButtonSelected)}
                                icon={!values.statuses?.length ? <FlagRegular /> : undefined}
                            >
                                {values.statuses?.length ? `${values.statuses.length} ...` : undefined}
                            </Button>
                        </Tooltip>
                    </MenuTrigger>
                    <MenuPopover>
                        <MenuList>
                            {taskStatuses.map((status, index) => (
                                <MenuItemCheckbox
                                    key={index}
                                    name={'statuses'}
                                    value={status.alpha}
                                    onClick={() =>
                                        setValues({
                                            ...values,
                                            statuses: values.statuses.includes(status.alpha)
                                                ? values.statuses.filter(value => value !== status.alpha)
                                                : [...values.statuses, status.alpha],
                                        })
                                    }
                                >
                                    {getStatusName(status.alpha)}
                                </MenuItemCheckbox>
                            ))}
                        </MenuList>
                    </MenuPopover>
                </Menu>
            )}
        </div>
    )
}

export default function BaseTaskOverview(props: BaseTaskOverviewProps) {
    const axios = useAxios()
    const itemStyles = useItemStyles()
    const overviewStyles = useOverviewStyles()
    const roles = useRoles()
    const { currentUser } = useUser()
    const { viewport, isViewport } = useViewport()
    const { loaded: tasksLoaded, activeTaskStatusCodes, getStartStatus, getStatusName } = useTaskStatuses()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.OutlookNewWide), [viewport])

    const defaultFilterValues = useMemo<TaskFilterType>(
        () => ({
            from: '',
            to: '',
            assignees: roles.any(rolesTasks) && currentUser?.status === UserStatus.Enabled ? [currentUser?.id] : [],
            assigners: [],
            files: [],
            crmRelations: [],
            statuses: activeTaskStatusCodes,
            attributes: [],
            query: '',
            tag: '7days',
            ...props.fixedFilterValues,
            ...props.defaultFilterValues,
        }),
        [props.fixedFilterValues, props.defaultFilterValues, tasksLoaded],
    )

    const fixedColumnFilterValues = (viewType: ViewType, values: TaskFilterType): TaskFilterType[] => {
        return viewType === ViewType.ListView ? [{}] : values.statuses.map(status => ({ statuses: [status], title: getStatusName(status) }))
    }

    const countItems = (_column?: number, filterValues?: TaskFilterType): Promise<BaseOverviewCount> => {
        return axios.get('/task/count', { params: parseFilterValuesToParams(filterValues) }).then(response => response.data)
    }

    const loadItems = (
        _column?: number,
        filterValues?: TaskFilterType,
        sortingValues?: TaskSortingType,
        start?: number,
        size?: number,
    ): Promise<ExtendedTaskOverviewDto[]> => {
        return axios
            .get('/task', { params: { ...parseFilterValuesToParams(filterValues, sortingValues, start, size) } })
            .then((response: AxiosResponse<ExtendedTaskOverviewDto[]>) => response?.data || [])
    }

    return (
        <BaseOverview<ExtendedTaskOverviewDto, TaskFilterType, TaskSortingType>
            defaultFilterValues={defaultFilterValues}
            fixedColumnFilterValues={fixedColumnFilterValues}
            countItems={countItems}
            loadItems={loadItems}
            listColumnFullWidth
            listItemHeaderContent={BaseHeaderItem}
            listItemHeaderClassnames={[overviewStyles.listBaseHeaderItem]}
            listItemContent={(lineProps: BaseTaskOverviewLineItemProps) => <TaskListItem {...lineProps} />}
            listItemClassnames={(lineProps: BaseTaskOverviewLineItemProps) => [
                itemStyles.taskItem,
                lineProps.viewType === ViewType.ListView ? itemStyles.listTaskItem : itemStyles.listTaskItemSmall,
                lineProps.viewType === ViewType.ListView && listItemSmall && itemStyles.listTaskItemSmall,
                lineProps.item?.highlight && itemStyles.listTaskItemHighlight,
                lineProps.item &&
                    lineProps.item.status === getStartStatus().alpha &&
                    compareDate(lineProps.item.due, DateTime.now().startOf('day')) < 0 &&
                    overviewStyles.colorOrange,
            ]}
            headerDefaultFilterTags={HeaderFilterTags}
            headerSortingItems={headerSortingItems}
            headerActionsButtons={HeaderActionsButtons}
            filterContent={TaskFilter}
            filterFromJson={values => {
                return {
                    ...values,
                    from: values.tag ? generateFromDate(values.tag) : values.from,
                    to: values.tag ? generateToDate(values.tag) : values.to,
                }
            }}
            filterToJson={values => {
                return {
                    ...values,
                    from: values.tag ? '' : values.from,
                    to: values.tag ? '' : values.to,
                    icon: undefined,
                    title: undefined,
                }
            }}
            inputPlaceholderKey={'task.filter.search'}
            kanbanEmptyPlaceholderKey={'task.view.empty'}
            {...props}
        />
    )
}
